import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import { PurchaseOrder, PurchaseOrderInsert, PurchaseOrderSearchCommand } from '../../models/purchase-order/purchase-order';
import { S3Object } from '../../models/invoice/invoice';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/purchase-order';

  getPurchaseOrder(): Observable<ApiResponse<PurchaseOrder[]>> {
    return this.httpClient.get<ApiResponse<PurchaseOrder[]>>(`${this.baseUrl}/all`);
  }

  getPurchaseOrderById(id: string): Observable<ApiResponse<PurchaseOrder>> {
    return this.httpClient.get<ApiResponse<PurchaseOrder>>(`${this.baseUrl}/${id}`);
  }

  createPurchaseOrder(purchaseOrders: PurchaseOrderInsert): Observable<ApiResponse<PurchaseOrder>> {
    return this.httpClient.post<ApiResponse<PurchaseOrder>>(this.baseUrl, purchaseOrders);
  }

  updatePurchaseOrder(purchaseOrder: PurchaseOrder): Observable<ApiResponse<PurchaseOrder>> {
    return this.httpClient.put<ApiResponse<PurchaseOrder>>(`${this.baseUrl}/${purchaseOrder.id}`, purchaseOrder);
  }

  searchPurchaseOrder(
    command: PurchaseOrderSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
  ): Observable<ApiResponse<PaginatedApiResponse<PurchaseOrder>>> {
    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder !== undefined) params = params.set('sortOrder', sortOrder.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<PurchaseOrder>>>(`${this.baseUrl}/search`, command, { params });
  }

  getPurchaseOrderV2(id: string) {
    return this.httpClient.get<ApiResponse<PurchaseOrder>>(`${this.baseUrl}/${id}`).pipe(map((response) => response.data));
  }

  getPurchaseOrderByNumberCode(numberCode: string) {
    return this.httpClient
      .post<ApiResponse<PaginatedApiResponse<PurchaseOrder>>>(`${this.baseUrl}/search`, {
        purchaseOrderNumber: `${numberCode}`,
      })
      .pipe(map((response) => response.data?.items));
  }

  public uploadFiles(files: File[], invoiceID: string): Observable<S3Object[]> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append(`files`, file, file.name);
    });

    return this.httpClient
      .post<{ success: boolean; data: S3Object[]; MessageError: string }>(`${this.baseUrl}/upload-files/${invoiceID}`, formData)
      .pipe(map(({ data }) => data));
  }

  public deleteFile(idInvoice: string, file: S3Object): Observable<ApiResponse<void>> {
    return this.httpClient.post<ApiResponse<void>>(`${this.baseUrl}/delete-files/${idInvoice}`, [file]);
  }

  public downloadReport(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/${id}/report`, { responseType: 'blob' });
  }

  public changeStatus(id: string, status: number): Observable<ApiResponse<PurchaseOrder>> {
    return this.httpClient.patch<ApiResponse<PurchaseOrder>>(`${this.baseUrl}/${id}/status`, status);
  }
}
